import React, { useEffect, useState } from "react";
import panda from "../../../../../../assets/PandaProfile.png";
import GlassPinboard from "./components/GlassPinboard";
import Top3ValuesBoard from "./components/Top3ValuesBoard";
import StickyNote from "./StickyNote";

const Top3SelectionScreen = ({ 
  selectedValues = [], 
  initialTop3 = [],
  onFinish, 
  onToggleImmersive,
  loading = false,
  isSaving = false
}) => {
  const [top3, setTop3] = useState(initialTop3);

  useEffect(() => {
    if (initialTop3 && initialTop3.length > 0) {
      setTop3(initialTop3);
    }
  }, [initialTop3]);

  useEffect(() => {
    if (typeof onToggleImmersive === "function") onToggleImmersive(true);
    return () => {
      if (typeof onToggleImmersive === "function") onToggleImmersive(false);
    };
  }, [onToggleImmersive]);

  const handleNoteClick = (value) => {
    if (top3.includes(value)) {
      setTop3((prev) => prev.filter((v) => v !== value));
    } else if (top3.length < 3) {
      setTop3((prev) => [...prev, value]);
    }
  };

  const getPosition = (index) => {
    if (index < 12) {
      const col = Math.floor(index / 3);
      const row = index % 3;
      return {
        top: 65 + row * 140,
        left: 50 + col * 140,
        colorIndex: col,
        zIndex: 1,
      };
    } else if (index < 18) {
      const offsetIndex = index - 12;
      const col = Math.floor(offsetIndex / 2);
      const row = offsetIndex % 2;
      const offsetColorMap = [2, 3, 0];
      return {
        top: 75 + row * 140 + 60,
        left: 40 + col * 150 + 80,
        colorIndex: offsetColorMap[col % 3],
        zIndex: 2,
      };
    } else {
      return null;
    }
  };

  return (
    <div className="w-full flex flex-col items-start justify-start pt-6 pb-16">
      {/* Saving indicator */}
      {isSaving && (
        <div className="fixed top-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 z-50">
          <span className="text-xs text-gray-600 font-gotham animate-pulse flex items-center gap-2">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        </div>
      )}

      {/* Panda and Heading */}
      <div className="flex items-center gap-4 mb-6 pl-2">
        <img
          src={panda}
          alt="Panda"
          style={{ width: 100.64, height: 100.64 }}
          draggable={false}
        />
        <div>
          <h2
            style={{
              fontFamily: "Gotham, sans-serif",
              fontWeight: 500,
              fontSize: 28,
              color: "#232323",
              lineHeight: "1.35",
            }}
            className="mb-1 ml-4"
          >
            From the selected values, choose your top 3 values
          </h2>
          <p
            style={{
              fontFamily: "Gotham, sans-serif",
              fontWeight: 400,
              fontSize: 15,
              color: "#888888",
              lineHeight: "1.65",
            }}
            className="ml-4"
          >
            Click to select your top 3 values
          </p>
        </div>
      </div>

      {/* Two Pinboards Side by Side */}
      <div className="flex items-start ml-8 mt-10 gap-8">
        {/* Left: Main Pinboard - Shows ALL selectedValues */}
        <GlassPinboard>
          {selectedValues.slice(0, 18).map((value, index) => {
            const position = getPosition(index);
            if (!position) return null;

            const isSelected = top3.includes(value);
            const rotation = (index % 5) - 2 + Math.random() * 0.6;

            return (
              <React.Fragment key={`${value}-${index}`}>
                <StickyNote
                  text={value}
                  style={{
                    top: position.top,
                    left: position.left,
                    zIndex: position.zIndex,
                  }}
                  colorIndex={position.colorIndex}
                  rotation={rotation}
                  onClick={() => handleNoteClick(value)}
                />

                {/* Checkmark overlay for selected note */}
                {isSelected && (
                  <div
                    style={{
                      position: "absolute",
                      top: position.top + 70,
                      left: position.left + 40,
                      zIndex: position.zIndex + 20,
                      pointerEvents: "none",
                      width: 17,
                      height: 17,
                      borderRadius: "50%",
                      background: "#000000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="8.78"
                      height="6.88"
                      viewBox="0 0 9 7"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        marginTop: 1.83,
                        marginLeft: 0.8,
                      }}
                    >
                      <path
                        d="M1 3.5L3.5 6L8 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </GlassPinboard>

        {/* Right: NEW Top3 Values Board */}
        <Top3ValuesBoard 
          values={top3} 
          onValueClick={(value) => {
            setTop3((prev) => prev.filter((v) => v !== value));
          }}
        />
      </div>

      {/* Bottom Button */}
      <div className="pl-8 mt-8">
        <button
          onClick={() => onFinish?.(top3)}
          disabled={top3.length !== 3 || loading}
          style={{
            width: 284,
            height: 69,
            borderRadius: 20,
            background: (top3.length === 3 && !loading) ? "#54B0AF" : "#CCCCCC",
            paddingTop: 22,
            paddingBottom: 22,
            paddingLeft: 20,
            paddingRight: 20,
            fontFamily: "Gotham, sans-serif",
            cursor: (top3.length === 3 && !loading) ? "pointer" : "not-allowed",
          }}
          className="flex items-center justify-between text-white font-medium text-[21px] shadow hover:opacity-90 transition"
        >
          <span className="font-gotham font-medium">{loading ? "Saving..." : "Finish Selection"}</span>
          <span className="ml-4 flex items-center justify-center">
            <span
              className="flex items-center justify-center"
              style={{
                background: "#fff",
                borderRadius: "50%",
                width: 20,
                height: 20,
              }}
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-[#54B0AF] border-t-transparent rounded-full"></div>
              ) : (
                <svg width="18" height="18" fill="#54B0AF" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};

export default Top3SelectionScreen;